import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePermission } from '../../../../auth/AuthContext';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { DateCell } from '../../../../components/table/DateCell';
import { useListSurface } from '../../../../components/table/listSurface';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import {
  personDisplayName,
  usePersonNames,
  type PersonNames,
} from '../../../human-resources/personnel/usePersonNames';
import { employeeAssetsApi } from '../employeeAssetApi';
import type { EmployeeAssetListItem } from '../employeeAssetTypes';

/**
 * Employee Assets — every employee, and how much of the issuance register stands against each.
 *
 * **The rows are PERSONNEL records**, so an employee who has been issued nothing is listed with
 * zeros: the question is "what does each person hold", and a person holding nothing is an
 * answer. The figures are counted under the rule the profile's lines are selected by, so the two
 * agree — a server test holds them to it.
 *
 * **A VIEW, so no Create, no Edit, no Delete and no Clear data.** Issuing and returning are done
 * on the profile under the issuance register's own keys.
 *
 * **The Name column carries `personnel.read`**, because a name is resolved through
 * `/api/persons/resolve` under that key; a reader without it sees employee numbers, which is
 * why the anchor is the number rather than the name. A record above the reader's personnel
 * access level arrives with no `personId` and reads `Restricted` — still listed and counted.
 */
export function EmployeeAssetsIndexPage() {
  const navigate = useNavigate();
  const canReadPersonnel = usePermission('personnel.read');

  // The ids on screen, captured on the way past the shell's fetch so they can be resolved in one
  // batch — the personnel list's own composition.
  const [pageIds, setPageIds] = useState<string[]>([]);
  const names = usePersonNames(canReadPersonnel ? pageIds : []);

  const fetchEmployees = useCallback(async (query: IndexQueryState, signal: AbortSignal) => {
    const page = await employeeAssetsApi.list(
      {
        search: query.search,
        page: query.page,
        pageSize: query.pageSize,
        sort: query.sort ?? undefined,
        status: query.facets.status?.join(','),
      },
      signal,
    );

    setPageIds(page.items.map((x) => x.personId).filter((id): id is string => id != null));

    return page;
  }, []);

  const columns = useMemo(() => buildColumns(names, canReadPersonnel), [names, canReadPersonnel]);

  return (
    <IndexPage<EmployeeAssetListItem>
      module="employee-assets"
      breadcrumb={['Logistics', 'Inventory', 'Employee Assets']}
      columns={columns}
      facets={[{ key: 'status', label: 'Status' }]}
      /*
        What this page reads is the issuance register, so its History Logs is the register's —
        every type the issuance workflow writes.
      */
      auditEntityTypes={[
        'Issuance',
        'IssuanceItem',
        'IssuanceBatchAllocation',
        'IssuanceReturn',
        'StockMovement',
      ]}
      rowKey={(employee) => employee.id}
      fetch={fetchEmployees}
      // The search reaches the employee number only — a name lives in another database.
      searchPlaceholder="Search employee numbers"
      emptyMessage="No personnel records yet. Employees are added under Human Resources."
      onRowOpen={(employee) => navigate(`/inventory/employee-assets/${employee.id}`)}
      openPermission="employee-assets.read"
      /*
        The row is a PERSONNEL record somebody wrote, not a derived one — the test frontend-tables
        sets — so it carries that record's provenance like the personnel list does.
      */
      otherInfo={(employee) => employee}
    />
  );
}

/**
 * The row's title. In the TABLE it is the employee number alone, beside its own Name column; on
 * the LIST, which has no Name column, the name is joined to it — `EMP-2026-0002 — Surname, Given`
 * — through `<TextCell code>`, so the number stays mono and the name stays prose.
 *
 * `name` is null without `personnel.read`, and the title is then the number alone: the name is
 * withheld, not blank. The Goods Receipts anchor reads the surface the same way.
 */
function EmployeeAnchor({
  employee,
  name,
}: {
  employee: EmployeeAssetListItem;
  name: string | null;
}) {
  const onList = useListSurface();

  // While a name is still resolving `personDisplayName` falls back to the number — never print it twice.
  return onList && name && name !== employee.employeeNumber ? (
    <TextCell code={employee.employeeNumber} value={name} />
  ) : (
    <TextCell value={employee.employeeNumber} mono />
  );
}

/** Built per render because the Name column reads resolved names. */
function buildColumns(
  names: PersonNames,
  canReadPersonnel: boolean,
): ColumnDef<EmployeeAssetListItem>[] {
  return [
    {
      key: 'employeeNumber',
      header: 'Employee no.',
      width: 170,
      sortable: true,
      render: (employee) => (
        <EmployeeAnchor
          employee={employee}
          name={canReadPersonnel ? personDisplayName(employee, names) : null}
        />
      ),
    },
    {
      /*
        Not sortable and not searchable — the values are in `rsys_pii`, and the server's sort
        allow-list says so. Gated on the key the resolve needs, so a reader without it meets no
        column rather than a column of employee numbers printed twice.
      */
      key: 'name',
      header: 'Name',
      width: 260,
      flex: true,
      permission: 'personnel.read',
      // NOT a summary value: the list view already joins the name into the title.
      render: (employee) => <TextCell value={personDisplayName(employee, names)} />,
    },
    {
      // Every live slip line naming them — the count the profile's table lists.
      key: 'issuedCount',
      header: 'Issued assets',
      width: 130,
      align: 'right',
      sortable: true,
      render: (employee) => <TextCell value={String(employee.issuedCount)} mono numeric />,
    },
    {
      // Issued less returned, on custody lines — a dispense is used up and never outstanding.
      key: 'outstandingQuantity',
      header: 'Outstanding',
      width: 130,
      align: 'right',
      sortable: true,
      render: (employee) => <TextCell value={String(employee.outstandingQuantity)} mono numeric />,
    },
    {
      key: 'lastIssuedAt',
      header: 'Last issued',
      width: 190,
      sortable: true,
      summary: true,
      render: (employee) => <DateCell value={employee.lastIssuedAt} fallback="Never" />,
    },
    {
      // The PERSONNEL record's own live/deleted state.
      key: 'status',
      header: 'Status',
      width: 120,
      status: true,
      render: (employee) => (
        <StatusText tone={employee.isActive ? 'active' : 'inactive'}>
          {employee.isActive ? 'Active' : 'Deleted'}
        </StatusText>
      ),
    },
  ];
}
